import "../../../css/LlmSettings.css";
import React, { useState, useEffect, useCallback } from "react";
import { Input, InputNumber, Button, Spin, message, Select, Alert, Tabs } from "antd";
import {
  getLlmSettings, updateLlmSettings, getAttributes,
  getRestrictedTerms, addRestrictedTerm, deleteRestrictedTerm,
  getCompetitorTerms, addCompetitorTerm, deleteCompetitorTerm,
  getAllowedTerms, addAllowedTerm, deleteAllowedTerm,
  getOutOfScopeResponse, updateOutOfScopeResponse,
  getChannelSettings, updateChannelSetting,
} from "../../../services/polly/llmSettingsService";
import {
  CHANNEL_LLM_MODE_OPTIONS,
  UNRECOGNIZED_CHANNEL_LLM_MODE_LABEL,
  isRecognizedChannelLlmMode,
  channelModeShortLabel,
  channelModeHint,
} from "../../../constants/Polly/ChannelLlmModes";
import { useSelector } from "react-redux";
import { useActiveCompanyData } from "hooks/CompanyHooks";
import ChannelOverrides from "./ChannelOverrides";

const { TextArea } = Input;
const { Option } = Select;

export default function LlmSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [attributes, setAttributes] = useState([]);
  const [restrictedTerms, setRestrictedTerms] = useState([]);
  const [competitorTerms, setCompetitorTerms] = useState([]);
  const [newRestricted, setNewRestricted] = useState({ term: '', reason: '' });
  const [newCompetitor, setNewCompetitor] = useState({ term: '', reason: '' });
  const [allowedTerms, setAllowedTerms] = useState([]);
  const [newAllowed, setNewAllowed] = useState({ term: '', reason: '' });
  const [outOfScopeText, setOutOfScopeText] = useState('');
  const [outOfScopeDirty, setOutOfScopeDirty] = useState(false);
  const [savingOutOfScope, setSavingOutOfScope] = useState(false);
  const [channelSettings, setChannelSettings] = useState([]);
  const [savingChannelId, setSavingChannelId] = useState(null);
  // Status per failed load, so a section can say why it is empty instead of looking unconfigured.
  const [loadErrors, setLoadErrors] = useState({});
  const { roAdmin } = useSelector((state) => state.loginReducer);
  // Customer search keys on the client's ARN; the settings routes key on its uuid.
  const activeCompanyData = useActiveCompanyData();

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLlmSettings();
      setSettings(res);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSettings({
          llmToneVoice: '',
          llmResponseLength: 'concise',
          maxMessageLength: 2500,
          maxConversationMessages: 10,
          commercialConsentAttributeId: null,
          ragSimilarityThreshold: null,
          maxRagChunks: null,
        });
      } else {
        message.error("Failed to load settings");
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const noteFailure = (key) => (err) =>
      setLoadErrors((prev) => ({ ...prev, [key]: err?.response?.status ?? 0 }));

    fetchSettings();
    getAttributes()
      .then((res) => setAttributes(Array.isArray(res) ? res : []))
      .catch((err) => { setAttributes([]); noteFailure('attributes')(err); });
    getRestrictedTerms()
      .then((res) => setRestrictedTerms(Array.isArray(res) ? res : []))
      .catch((err) => { setRestrictedTerms([]); noteFailure('restricted')(err); });
    getCompetitorTerms()
      .then((res) => setCompetitorTerms(Array.isArray(res) ? res : []))
      .catch((err) => { setCompetitorTerms([]); noteFailure('competitor')(err); });
    getAllowedTerms()
      .then((res) => setAllowedTerms(Array.isArray(res) ? res : []))
      .catch((err) => { setAllowedTerms([]); noteFailure('allowed')(err); });
    getOutOfScopeResponse()
      .then((res) => setOutOfScopeText(res?.text || ''))
      .catch((err) => { setOutOfScopeText(''); noteFailure('outOfScope')(err); });
    getChannelSettings()
      .then((res) => setChannelSettings(Array.isArray(res) ? res : []))
      .catch(noteFailure('channels'));
  }, [fetchSettings]);

  const update = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // llmEnabled is a BluStream-operated switch with no control on this screen. The endpoint
      // merges by key presence, so omitting it leaves the stored value alone.
      const body = { ...settings };
      delete body.llmEnabled;
      const res = await updateLlmSettings(body);
      setSettings(res);
      setDirty(false);
      message.success("Settings saved");
    } catch (err) {
      message.error("Failed to save settings");
    }
    setSaving(false);
  };

  const handleChannelModeChange = async (row, llmMode) => {
    const previous = row.llmMode;
    setChannelSettings((prev) => prev.map((c) => (c.id === row.id ? { ...c, llmMode } : c)));
    setSavingChannelId(row.id);
    try {
      const res = await updateChannelSetting(row.id, llmMode);
      setChannelSettings((prev) => prev.map((c) => (c.id === row.id ? { ...c, ...res } : c)));
      message.success(`${row.displayName} updated`);
    } catch (err) {
      setChannelSettings((prev) =>
        prev.map((c) => (c.id === row.id ? { ...c, llmMode: previous } : c)));
      const detail = err?.response?.data?.error;
      message.error(detail || `Failed to update ${row.displayName}`);
    }
    setSavingChannelId(null);
  };

  const handleSaveOutOfScope = async () => {
    setSavingOutOfScope(true);
    try {
      await updateOutOfScopeResponse(outOfScopeText);
      setOutOfScopeDirty(false);
      message.success("Out-of-scope response saved");
    } catch (err) {
      message.error("Failed to save out-of-scope response");
    }
    setSavingOutOfScope(false);
  };

  const handleAddRestricted = async () => {
    const term = newRestricted.term.trim();
    if (!term) return;
    try {
      const entry = await addRestrictedTerm(term, newRestricted.reason.trim() || null);
      setRestrictedTerms((prev) => [...prev, entry]);
      setNewRestricted({ term: '', reason: '' });
      message.success("Restricted term added");
    } catch (err) {
      message.error("Failed to add restricted term");
    }
  };

  const handleRemoveRestricted = async (rt) => {
    try {
      await deleteRestrictedTerm(rt.id);
      setRestrictedTerms((prev) => prev.filter((t) => t.id !== rt.id));
    } catch (err) {
      message.error("Failed to remove restricted term");
    }
  };

  const handleAddCompetitor = async () => {
    const term = newCompetitor.term.trim();
    if (!term) return;
    try {
      const entry = await addCompetitorTerm(term, newCompetitor.reason.trim() || null);
      setCompetitorTerms((prev) => [...prev, entry]);
      setNewCompetitor({ term: '', reason: '' });
      message.success("Competitor term added");
    } catch (err) {
      message.error("Failed to add competitor term");
    }
  };

  const handleRemoveCompetitor = async (ct) => {
    try {
      await deleteCompetitorTerm(ct.id);
      setCompetitorTerms((prev) => prev.filter((t) => t.id !== ct.id));
    } catch (err) {
      message.error("Failed to remove competitor term");
    }
  };

  const handleAddAllowed = async () => {
    const term = newAllowed.term.trim();
    if (!term) return;
    try {
      const entry = await addAllowedTerm(term, newAllowed.reason.trim() || null);
      setAllowedTerms((prev) => [...prev, entry]);
      setNewAllowed({ term: '', reason: '' });
      message.success("Allowed term added");
    } catch (err) {
      message.error("Failed to add allowed term");
    }
  };

  const handleRemoveAllowed = async (at) => {
    try {
      await deleteAllowedTerm(at.id);
      setAllowedTerms((prev) => prev.filter((t) => t.id !== at.id));
    } catch (err) {
      message.error("Failed to remove allowed term");
    }
  };

  if (!settings) {
    return (
      <div className="llm-settings-container">
        <div className="llm-settings-breadcrumb">Polly &rsaquo; <span>Settings</span></div>
        <Spin spinning={loading}><div style={{ minHeight: 200 }} /></Spin>
      </div>
    );
  }

  // Only an explicit false disables the client. An absent value means the client inherits the
  // system default, which is on.
  const llmDisabledByAdmin = settings.llmEnabled === false;

  // A refused read, rather than an empty one. Reported per section so an unreadable list is never
  // shown as a list the client has not filled in.
  const loadRefused = Object.values(loadErrors).includes(403);

  const loadFailureNote = (key) => {
    const status = loadErrors[key];
    if (status === undefined) return null;
    return (
      <div className="llm-settings-empty">
        {status === 403
          ? "Not available while LLM conversations are turned off for your account."
          : "This could not be loaded. Try again shortly."}
      </div>
    );
  };

  return (
    <div className="llm-settings-container">
      <div className="llm-settings-breadcrumb">Polly &rsaquo; <span>Settings</span></div>
      <div className="llm-settings-body">
        <div className="llm-settings-header">
          <h2>Polly Settings</h2>
          <Button type="primary" onClick={handleSave} loading={saving} disabled={!dirty}>
            Save Changes
          </Button>
        </div>

        {llmDisabledByAdmin && (
          <Alert
            style={{ marginBottom: 16 }}
            type="error"
            showIcon
            banner
            message={
              "LLM conversations are turned off for your account by BluStream. Until they are "
              + "turned back on, no channel runs them, and the channel controls are read-only."
              + (loadRefused
                ? " Some settings below cannot be loaded or changed while they are off."
                : "")
            }
          />
        )}

        {settings.requireCommercialConsent === true && !settings.commercialConsentAttributeId && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            banner
            message="Commercial consent is required for this client, but no consent attribute is configured. Until one is set, all customers are treated as non-consented and will not receive AI responses."
          />
        )}

        {/* Channels */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Channels</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Choose which of your customers can have LLM conversations on each channel, and any exceptions for individual customers. Each change saves immediately.
          </div>
          {llmDisabledByAdmin && (
            <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
              These settings are not in effect while LLM conversations are turned off for your account.
            </div>
          )}
          {loadErrors.channels !== undefined && (
            <div className="llm-settings-empty">
              {loadErrors.channels === 403
                ? "Not available while LLM conversations are turned off for your account."
                : "Channel settings could not be loaded."}
            </div>
          )}
          {loadErrors.channels === undefined && channelSettings.length === 0 && (
            <div className="llm-settings-empty">No channels are set up for your account.</div>
          )}
          {loadErrors.channels === undefined && channelSettings.length > 0 && (
            <Tabs
              className="llm-settings-tabs"
              items={channelSettings.map((row) => {
                const recognized = isRecognizedChannelLlmMode(row.llmMode);
                const showRawMode = !recognized && !!row.llmMode;
                const locked = llmDisabledByAdmin || roAdmin;
                return {
                  key: row.channelType,
                  label: (
                    <span>
                      <span>{row.displayName}</span>
                      <span className="llm-settings-tab-mode">{` · ${channelModeShortLabel(row.llmMode)}`}</span>
                    </span>
                  ),
                  children: (
                    <>
                      <div className="llm-settings-row">
                        <div className="llm-settings-field">
                          <div className="llm-settings-label">{`Who Polly replies to on ${row.displayName}`}</div>
                          {recognized && (
                            <div className="llm-settings-hint">{channelModeHint(row.llmMode, row.displayName)}</div>
                          )}
                          {showRawMode && (
                            <div className="llm-settings-hint">
                              This channel has a setting the portal does not recognize. Choose an option to replace it.
                            </div>
                          )}
                        </div>
                        <Select
                          value={row.llmMode || undefined}
                          onChange={(val) => handleChannelModeChange(row, val)}
                          loading={savingChannelId === row.id}
                          disabled={locked || savingChannelId === row.id}
                          placeholder="Select an option"
                          style={{ width: 300 }}
                        >
                          {showRawMode && (
                            <Option value={row.llmMode}>{UNRECOGNIZED_CHANNEL_LLM_MODE_LABEL}</Option>
                          )}
                          {CHANNEL_LLM_MODE_OPTIONS.map((opt) => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </div>
                      <ChannelOverrides
                        channel={row}
                        customerArn={activeCompanyData?.arn}
                        customerId={activeCompanyData?.databaseId}
                        locked={locked}
                      />
                    </>
                  ),
                };
              })}
            />
          )}
        </div>

        {/* Conversation Behavior */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Conversation Behavior</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Tone and Voice</div>
              <div className="llm-settings-hint">How Polly communicates — affects word choice, formality, and personality</div>
            </div>
            <Input
              value={settings.llmToneVoice || ''}
              onChange={(e) => update('llmToneVoice', e.target.value)}
              placeholder="e.g. friendly and professional"
              style={{ width: 300 }}
            />
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Response Length</div>
              <div className="llm-settings-hint">How detailed Polly's responses should be</div>
            </div>
            <Select
              value={settings.llmResponseLength || 'concise'}
              onChange={(val) => update('llmResponseLength', val)}
              style={{ width: 200 }}
            >
              <Option value="brief">Brief (1-2 sentences)</Option>
              <Option value="concise">Concise (2-4 sentences)</Option>
              <Option value="detailed">Detailed (full explanation)</Option>
            </Select>
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Out-of-Scope Response</div>
              <div className="llm-settings-hint">Message sent when a question is outside Polly's knowledge base. This is stored as a message template. Leave empty to use the default.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <TextArea
                value={outOfScopeText}
                onChange={(e) => { setOutOfScopeText(e.target.value); setOutOfScopeDirty(true); }}
                placeholder="Default: I'm only able to help with questions about our products and services. For anything else, please reach out to our support team directly."
                rows={3}
                style={{ width: 400 }}
              />
              {outOfScopeDirty && (
                <Button size="small" type="primary" onClick={handleSaveOutOfScope} loading={savingOutOfScope}
                        style={{ alignSelf: 'flex-end' }}>
                  Save Response
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Limits */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Limits</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Max Message Length</div>
              <div className="llm-settings-hint">Maximum characters per inbound customer message. Messages exceeding this limit are truncated for LLM processing. The full message is still saved for audit.</div>
            </div>
            <InputNumber
              value={settings.maxMessageLength}
              onChange={(val) => update('maxMessageLength', val)}
              placeholder="2500"
              min={100}
              max={10000}
              style={{ width: 150 }}
            />
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Conversation History Window</div>
              <div className="llm-settings-hint">Number of recent messages included as context for the LLM. More history improves continuity but increases response time.</div>
            </div>
            <InputNumber
              value={settings.maxConversationMessages}
              onChange={(val) => update('maxConversationMessages', val)}
              placeholder="10"
              min={2}
              max={50}
              style={{ width: 150 }}
            />
          </div>
        </div>

        {/* RAG Tuning */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Document Retrieval</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Similarity Threshold</div>
              <div className="llm-settings-hint">Minimum similarity score for a document chunk to be included in the LLM prompt. Higher values return fewer but more relevant chunks.</div>
            </div>
            <Select
              value={settings.ragSimilarityThreshold || undefined}
              onChange={(val) => update('ragSimilarityThreshold', val || null)}
              placeholder="Default (0.65)"
              allowClear
              style={{ width: 200 }}
            >
              <Option value={0.50}>0.50 (Broad)</Option>
              <Option value={0.55}>0.55</Option>
              <Option value={0.60}>0.60</Option>
              <Option value={0.65}>0.65 (Default)</Option>
              <Option value={0.70}>0.70</Option>
              <Option value={0.75}>0.75</Option>
              <Option value={0.80}>0.80 (Strict)</Option>
              <Option value={0.85}>0.85</Option>
              <Option value={0.90}>0.90 (Very strict)</Option>
            </Select>
          </div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Max Document Chunks</div>
              <div className="llm-settings-hint">Maximum number of document chunks to include in the LLM prompt. Fewer chunks = faster responses but narrower context.</div>
            </div>
            <Select
              value={settings.maxRagChunks || undefined}
              onChange={(val) => update('maxRagChunks', val || null)}
              placeholder="Default (5)"
              allowClear
              style={{ width: 200 }}
            >
              <Option value={2}>2 (Minimal)</Option>
              <Option value={3}>3</Option>
              <Option value={5}>5 (Default)</Option>
              <Option value={7}>7</Option>
              <Option value={10}>10 (Maximum)</Option>
            </Select>
          </div>
        </div>

        {/* Access Control */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Access Control</div>

          <div className="llm-settings-row">
            <div className="llm-settings-field">
              <div className="llm-settings-label">Commercial Consent Attribute</div>
              <div className="llm-settings-hint">Controls whether Polly can include commercial content (pricing, promotions, upsells) in responses. When not set or when the contact's value is false, Polly responds with informational content only.</div>
              {loadFailureNote('attributes')}
            </div>
            <Select
              showSearch
              allowClear
              value={settings.commercialConsentAttributeId || undefined}
              onChange={(val) => update('commercialConsentAttributeId', val || null)}
              placeholder="Not set (informational responses only)"
              optionFilterProp="children"
              disabled={loadErrors.attributes !== undefined}
              style={{ width: 300 }}
            >
              {attributes.map((attr) => (
                <Option key={attr.id} value={attr.id}>{attr.name}</Option>
              ))}
            </Select>
          </div>

        </div>

        {/* Restricted Terms */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Restricted Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Terms that Polly must never use in responses. Violations trigger rephrasing or blocking.
          </div>
          {restrictedTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {restrictedTerms.map((rt) => (
                <span key={rt.id} className="llm-settings-chip">
                  {rt.term}
                  {rt.reason && <span className="llm-settings-chip-reason" title={rt.reason}>({rt.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveRestricted(rt)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            loadFailureNote('restricted')
            || <div className="llm-settings-empty">No restricted terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newRestricted.term}
              onChange={(e) => setNewRestricted((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="Term"
              style={{ flex: 2 }}
              onPressEnter={handleAddRestricted}
            />
            <Input
              value={newRestricted.reason}
              onChange={(e) => setNewRestricted((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddRestricted}
            />
            <Button onClick={handleAddRestricted} disabled={!newRestricted.term.trim()}>Add</Button>
          </div>
        </div>

        {/* Competitor Terms */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Competitor Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Competitor terms that Polly should avoid mentioning.
          </div>
          {competitorTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {competitorTerms.map((ct) => (
                <span key={ct.id} className="llm-settings-chip">
                  {ct.term}
                  {ct.reason && <span className="llm-settings-chip-reason" title={ct.reason}>({ct.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveCompetitor(ct)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            loadFailureNote('competitor')
            || <div className="llm-settings-empty">No competitor terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newCompetitor.term}
              onChange={(e) => setNewCompetitor((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="Term"
              style={{ flex: 2 }}
              onPressEnter={handleAddCompetitor}
            />
            <Input
              value={newCompetitor.reason}
              onChange={(e) => setNewCompetitor((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddCompetitor}
            />
            <Button onClick={handleAddCompetitor} disabled={!newCompetitor.term.trim()}>Add</Button>
          </div>
        </div>

        {/* Allowed Terms (PII whitelist) */}
        <div className="llm-settings-section">
          <div className="llm-settings-section-title">Allowed Terms</div>
          <div className="llm-settings-hint" style={{ marginBottom: 8 }}>
            Values that should not be redacted by PII detection, such as your support phone number or email address. Phone numbers are normalized for matching regardless of format.
          </div>
          {allowedTerms.length > 0 ? (
            <div className="llm-settings-chip-list">
              {allowedTerms.map((at) => (
                <span key={at.id} className="llm-settings-chip">
                  {at.term}
                  {at.reason && <span className="llm-settings-chip-reason" title={at.reason}>({at.reason})</span>}
                  <button className="llm-settings-chip-remove" onClick={() => handleRemoveAllowed(at)}>&times;</button>
                </span>
              ))}
            </div>
          ) : (
            loadFailureNote('allowed')
            || <div className="llm-settings-empty">No allowed terms configured.</div>
          )}
          <div className="llm-settings-add-row">
            <Input
              value={newAllowed.term}
              onChange={(e) => setNewAllowed((prev) => ({ ...prev, term: e.target.value }))}
              placeholder="e.g. +15551234567 or support@acme.com"
              style={{ flex: 2 }}
              onPressEnter={handleAddAllowed}
            />
            <Input
              value={newAllowed.reason}
              onChange={(e) => setNewAllowed((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder="Reason (optional)"
              style={{ flex: 3 }}
              onPressEnter={handleAddAllowed}
            />
            <Button onClick={handleAddAllowed} disabled={!newAllowed.term.trim()}>Add</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
